import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert } from "@mui/material";
import PageLayout from "../../../../components/PageLayout";
import { DocumentReviewDialog } from "../../components/DocumentReviewDialog";
import { useDocumentListWithData } from "../../hooks/useDocumentListWithData";
import { PageVerificationContent } from "../../components/PageVerificationContent";
import { ApplicantSubtitle } from "../../components/ApplicantSubtitle";
import { getTitle } from "../../utils/getTitle";
import { DocumentRequestInfoDialog } from "../../components/DocumentRequestInfoDialog";
import { useVerificationHistory } from "../../hooks/useVerificationHistory";
import { GenerateLinkButton } from "../../components/GenerateLinkButton";
import { ApplicantSummaryButton } from "../../components/ApplicantSummaryButton";
import { CardTitleGrid } from "./styles";
import { useWidth } from "../../../app/hooks/useWidth";
import { SendToVerificationDialog } from "../../components/SendToVerificationDialog";
import { compareIsNamesEqual } from "./compareNames";
import { useCompanyAuth } from "../../../company/hooks/useCompanyAuth";
import { useCompaniesList } from "../../../company/hooks/useCompaniesList";
import { useComplianceComments } from "../../hooks/useComplianceComments";
import { ChangeStatusReasonDialog } from "../../components/ChangeStatusReasonDialog";
import { useGetUserInfo } from "../../../regexConfig/hooks/useGetUserInfo";
import { BinaryDataErrorDialog } from "../../components/BinaryDataErrorDialog";
import { RETURN_TO_REVISION, VerificationStatus } from "../../utils/getStatus";
import { useVerificationStatusActions } from "../../hooks/useVerificationStatusActions";
import { useGetCompanyVerificationStatuses } from "../../hooks/useGetCompanyVerificationStatuses";
import { ChangeRiskLevelDialog } from "../../components/ChangeRiskLevelDialog";
import { useAppSettings } from "../../../app/hooks/useAppSettings";
import { useApp } from "../../../app/hooks/useApp";
import { TOnboardingConfig } from "../../../app/models";
import { OnboardingConfigProvider } from "../../context/OnboardingConfigContext";
import { useVerificationRequestAccess } from "../../hooks/useVerificationRequestAccess";

enum IdentificationDocTypeView {
  "Id" = "ID",
  "Passport" = "Passport",
}

export const VerificationRequestScreen = memo(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isBinaryDataDialogShow, setIsBinaryDataDialogShow] = useState(false);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isRequestInfoOpen, setIsRequestInfoOpen] = useState(false);
  const [isSendToVerificationOpen, setIsSendToVerificationOpen] =
    useState(false);
  const [isChangeStatusReasonOpen, setIsChangeStatusReasonOpen] =
    useState(false);
  const [isChangeRiskLevelOpen, setIsChangeRiskLevelOpen] = useState(false);
  const [value, setValue] = useState<string>("");
  const [nextStatus, setNextStatus] = useState<VerificationStatus | null>(null);
  const [identificationDocumentType, setIdentificationDocumentType] = useState<
    "Passport" | "ID" | null
  >(null);
  const [documents, documentsLoading, documentsError, data, request] =
    useDocumentListWithData(id!);
  const [verificationRequestAccess] = useVerificationRequestAccess(id);
  const [comments, , , , addComment] = useComplianceComments(id!);
  const [actions, actionsLoading, , actionsRequest] =
    useVerificationStatusActions(id!);
  const [statusesConfig, statusesConfigLoading] =
    useGetCompanyVerificationStatuses();
  const [, , , getCompaniesList] = useCompaniesList(true);
  const [, , , , , , authHandlerToAnotherCompany] = useCompanyAuth();
  const [history, , , historyRequest, loadMoreHistory] = useVerificationHistory(
    id!,
    10
  );
  const [userInfo] = useGetUserInfo(data);
  const [app] = useApp(data?.appId ? String(data.appId) : undefined);
  const [appSettings, appSettingsLoading] = useAppSettings(
    data?.appId ? String(data.appId) : undefined
  );
  const onboardingConfig = useMemo<TOnboardingConfig | null>(() => {
    if (!appSettings?.onboardingConfig) return null;
    try {
      return JSON.parse(appSettings.onboardingConfig) as TOnboardingConfig;
    } catch {
      return null;
    }
  }, [appSettings?.onboardingConfig]);
  const width = useWidth();
  const location = useLocation();
  const [prevPage, anotherCompany] = useMemo(
    () => location.search.split("&"),
    [location]
  );
  const canEdit = verificationRequestAccess.mode === "update";

  useEffect(() => {
    if (anotherCompany && data) {
      authHandlerToAnotherCompany(data.companyId);
      getCompaniesList();
      navigate(`/verification-requests/${id}?page=${prevPage.split("=")[1] || 1}`, {
        replace: true,
      });
    }
  }, [
    anotherCompany,
    data,
    authHandlerToAnotherCompany,
    id,
    navigate,
    prevPage,
    getCompaniesList,
  ]);

  const isNamesEqual = useMemo(() => {
    if (data?.identificationStatus === "valid") {
      const userInfoData = documents?.find(
        (doc) => doc.document.documentType === "UserInformation"
      )?.data[0]?.data;
      const identityData = documents
        ?.filter(
          (doc) =>
            doc.document.documentType === "Passport" ||
            doc.document.documentType === "Id"
        )
        ?.map((docData) => {
          return docData.data.find((doc) => doc.type === "binary")!;
        });
      if (!identityData) {
        return false;
      }
      if (identityData?.length === 1) {
        try {
          setIdentificationDocumentType(
            IdentificationDocTypeView[
              identityData[0].dataId as "Id" | "Passport"
            ]
          );
        } catch (e) {
          setIsBinaryDataDialogShow(true);
        }
        return (
          compareIsNamesEqual(
            userInfoData?.first_name,
            identityData[0]?.data?.first_name
          ) &&
          compareIsNamesEqual(
            userInfoData?.last_name,
            identityData[0]?.data?.last_name
          )
        );
      } else {
        let equal = true;

        for (let indentity of identityData) {
          equal =
            compareIsNamesEqual(
              userInfoData?.first_name,
              indentity?.data?.first_name
            ) &&
            compareIsNamesEqual(
              userInfoData?.last_name,
              indentity?.data?.last_name
            );
          if (!equal) {
            try {
              setIdentificationDocumentType(
                IdentificationDocTypeView[indentity.dataId as "Id" | "Passport"]
              );
            } catch (e) {
              setIsBinaryDataDialogShow(true);
            } finally {
              break;
            }
          }
        }
        return equal;
      }
    } else {
      return false;
    }
  }, [documents, data?.identificationStatus]);

  const handleOpenReview = useCallback(
    (value: string, to?: VerificationStatus) => {
      if (!to) {
        value === "changeStatusReason"
          ? setIsChangeStatusReasonOpen(true)
          : setIsChangeRiskLevelOpen(true);
      } else {
        setValue(value);
        setNextStatus(to);
        if (
          [
            VerificationStatus.ACCEPTED,
            VerificationStatus.CANCELED,
            VerificationStatus.REJECTED,
            VerificationStatus.ON_FINAL_REVIEW,
          ].includes(to) ||
          value === RETURN_TO_REVISION
        ) {
          setIsReviewOpen(true);
        } else if (to === VerificationStatus.INFORMATION_REQUESTED) {
          setIsRequestInfoOpen(true);
        } else if (to === VerificationStatus.SENT_TO_VERIFICATION) {
          setIsSendToVerificationOpen(true);
        }
      }
    },
    [setIsReviewOpen]
  );

  const handleCloseReview = useCallback(
    (
      reason:
        | "cancel"
        | "changeStatusReason"
        | "changeRiskLevel"
        | VerificationStatus
    ) => {
      setIsReviewOpen(false);
      setIsRequestInfoOpen(false);
      setIsSendToVerificationOpen(false);
      setIsChangeStatusReasonOpen(false);
      setIsChangeRiskLevelOpen(false);
      if (reason === "cancel") {
        return;
      }
      if (reason === "changeStatusReason" || reason === "changeRiskLevel") {
        request();
        return;
      }
      if (
        [
          VerificationStatus.ACCEPTED,
          VerificationStatus.CANCELED,
          VerificationStatus.REJECTED,
          VerificationStatus.INFORMATION_REQUESTED,
          VerificationStatus.ON_FINAL_REVIEW,
        ].includes(reason)
      ) {
        if (window.history.state && window.history.state.idx > 0) {
          navigate(-1);
        } else {
          navigate("/verification-requests");
        }
      } else if ([VerificationStatus.SENT_TO_VERIFICATION].includes(reason)) {
        request();
        actionsRequest();
        historyRequest();
      }
    },
    [setIsReviewOpen, actionsRequest, request, historyRequest, navigate]
  );

  let errorMessage: string | null = null;
  if (documentsError) {
    errorMessage = documentsError.toString();
  }

  return (
    <PageLayout
      title={getTitle(id!, data)}
      subTitle={
        <ApplicantSubtitle
          statusesConfig={statusesConfig}
          deletedAt={data?.deletedAt || null}
          status={data?.status}
          category={data?.category}
          appId={data?.appId ? String(data.appId) : undefined}
          appName={app?.name}
          createdAt={data?.createdAt}
        />
      }
      backUrl={`/verification-requests/page/${prevPage.split("=")[1] || 1}#${id}`}
      onlyUrl
      actions={
        !["xs", "sm"].includes(width) ? (
          <CardTitleGrid>
            <ApplicantSummaryButton
              statusesConfig={statusesConfig}
              userInfo={userInfo}
              comments={comments}
              documents={documents}
              data={data}
              id={id}
              onboardingConfig={onboardingConfig}
            />
            <GenerateLinkButton
              id={data?.id}
              appPublicKey={data?.appPublicKey ?? ""}
            />
          </CardTitleGrid>
        ) : null
      }
      loading={documentsLoading || statusesConfigLoading}
      empty={documentsError === null && data === null}
    >
      <OnboardingConfigProvider config={onboardingConfig} isLoading={appSettingsLoading}>
        {errorMessage !== null && <Alert severity="error">{errorMessage}</Alert>}
        {["xs", "sm"].includes(width) ? (
          <CardTitleGrid>
            <ApplicantSummaryButton
              statusesConfig={statusesConfig}
              userInfo={userInfo}
              comments={comments}
              documents={documents}
              data={data}
              id={id}
              onboardingConfig={onboardingConfig}
            />
            <GenerateLinkButton
              id={data?.id}
              appPublicKey={data?.appPublicKey ?? ""}
            />
          </CardTitleGrid>
        ) : null}

        <PageVerificationContent
          actions={actions}
          userInfo={userInfo}
          comments={comments}
          addComment={addComment}
          statusesConfig={statusesConfig}
          loading={documentsLoading || statusesConfigLoading || actionsLoading}
          documents={documents}
          data={data}
          history={history}
          id={id}
          canEdit={canEdit}
          onAction={handleOpenReview}
          onRefresh={request}
          onLoadMoreHistory={loadMoreHistory}
        />
        <DocumentReviewDialog
          actions={actions.modalButtons}
          statusesConfig={statusesConfig}
          identificationDocumentType={identificationDocumentType}
          isNamesEqual={isNamesEqual}
          status={data?.screeningStatus ?? ""}
          verificationId={id!}
          open={isReviewOpen}
          onClose={handleCloseReview}
          value={value}
          nextStatus={nextStatus!}
          riskLevel={
            data?.riskLevel
              ? {
                  riskLevel: data.riskLevel,
                  riskLevelReason: data?.riskLevelReason,
                }
              : null
          }
        />
        <DocumentRequestInfoDialog
          verificationId={id!}
          open={isRequestInfoOpen}
          onClose={handleCloseReview}
          data={data}
          value={value}
        />
        <SendToVerificationDialog
          verificationId={id!}
          open={isSendToVerificationOpen}
          onClose={handleCloseReview}
          value={value}
          nextStatus={nextStatus!}
          statusesConfig={statusesConfig}
        />
        <ChangeStatusReasonDialog
          verificationId={id!}
          request={request}
          statusReason={data?.statusReason ?? ""}
          open={isChangeStatusReasonOpen}
          onClose={handleCloseReview}
        />
        <ChangeRiskLevelDialog
          verificationId={id!}
          request={request}
          riskLevel={data?.riskLevel}
          riskLevelReason={data?.riskLevelReason}
          open={isChangeRiskLevelOpen}
          onClose={handleCloseReview}
        />
        <BinaryDataErrorDialog
          open={isBinaryDataDialogShow}
          onClose={() => setIsBinaryDataDialogShow(false)}
        />
      </OnboardingConfigProvider>
    </PageLayout>
  );
});
